import { useCallback, useEffect, useRef } from 'react';
import { SIGNALR_LISTENERS } from '../../../../config/signalr';
import { useSignalRListener } from '../../../../hooks/useSignalR';

// One backend transition sends several events; refetching the whole workflow once covers them all.
const COALESCE_MS = 250;

export function useQuartersLiveRefresh({ workflowId, workflowPeriods, refetch }) {
  const matchPeriodInThisWorkflow = useCallback(
    payload => {
      if (!payload) return false;
      // WorkflowPeriodUpdate carries workflow_id; status events carry period_id.
      if (payload.workflow_id && payload.workflow_id !== workflowId) return false;
      if (payload.period_id) {
        const periodIds = (workflowPeriods?.periods ?? []).map(p => p.id);
        if (periodIds.length && !periodIds.includes(payload.period_id)) return false;
      }
      return true;
    },
    [workflowId, workflowPeriods]
  );

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSignal = useCallback(
    payload => {
      if (!matchPeriodInThisWorkflow(payload) || timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        refetchRef.current();
      }, COALESCE_MS);
    },
    [matchPeriodInThisWorkflow]
  );

  useSignalRListener(SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_APPROVAL_STATUS_UPDATED, handleSignal);
}
